import { motion } from "motion/react";
import {
  NavLink,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { api, type ClientPortal } from "../lib/api";
import { useResource, useAction } from "../lib/useResource";
import { useSession } from "../lib/session";
import {
  AbsentNotice,
  Button,
  Failed,
  SkeletonRows,
} from "../components/primitives";
import { portalSettles, prefersReducedMotion } from "../lib/motion";
import { BrandLockup } from "../components/BrandLockup";
import {
  PortalOverview,
  PortalActions,
  PortalProgress,
  PortalDeliverables,
  PortalFiles,
  PortalInvoices,
} from "./PortalPages";

/**
 * The client portal's shell.
 *
 * Grounded in ink, and that is the one real departure from the internal
 * surfaces. A client visits briefly and infrequently; this is the firm's face
 * and should look like the website that won the work, not like an admin tool
 * they have been given a login to.
 *
 * **It is six pages now, not one scroll.** The sidebar used to be anchor links
 * into one long document; `PortalPages.tsx` has the full reasoning, but the
 * short version is that two of the five anchors pointed at the wrong content,
 * and on a phone the sidebar was hidden entirely so there was no navigation at
 * all. The overview survives as the overview — it caps its lists and links
 * through — and each of Actions, Progress, Deliverables, Files and Invoices is
 * now a real place with its own URL that a client can be sent.
 *
 * **One fetch for all of it.** `/api/portal/:id` carries every section, so the
 * resource lives here on the shell and the pages receive it. Fetching the same
 * payload once per route would be five identical requests to show one
 * engagement. Invoices are the exception and fetch their own, because they
 * come from `/api/engagements/:id/bills`.
 */

/** The nav, in the order a client reads it: where it is, then what to do. */
const PAGES: Array<[string, string]> = [
  ["", "Overview"],
  ["actions", "Actions"],
  ["progress", "Progress"],
  ["deliverables", "Deliverables"],
  ["files", "Files"],
  ["invoices", "Invoices"],
];

export function Portal({ engagementId }: { engagementId: string }) {
  const { me, signOut } = useSession();
  const { resource, reload } = useResource<ClientPortal>(
    () => api.get<ClientPortal>(`/api/portal/${engagementId}`),
    [engagementId],
  );

  const respond = useAction(async (actionId: string) => {
    await api.post(`/api/client-actions/${actionId}/respond`);
  });

  const reduced = prefersReducedMotion();
  const base = `/e/${engagementId}`;
  const location = useLocation();
  const navigate = useNavigate();
  const activeSlug =
    PAGES.find(([slug]) =>
      slug
        ? location.pathname === `${base}/${slug}`
        : location.pathname === base || location.pathname === `${base}/`,
    )?.[0] ?? "";

  return (
    <div data-world="ink" className="min-h-screen bg-canvas text-ink lg:grid lg:grid-cols-[19rem_minmax(0,1fr)]">
      <aside className="border-b border-line bg-[#0d0d0d] px-6 py-6 lg:sticky lg:top-0 lg:flex lg:h-screen lg:flex-col lg:border-b-0 lg:border-r lg:px-8 lg:py-8">
        <BrandLockup caption="Client portal" />

        {/*
          The navigation, and the reason it is not `hidden lg:block` any more.

          It used to be: on a phone the client got no navigation whatsoever,
          just a long scroll, which is the device they are most likely to open
          an emailed link on. The later horizontal strip still made that header
          wider and noisier than its content. Below `lg`, the same six pages
          now live in the platform-native select; from `lg`, the visible rail
          remains.
          The desktop `NavLink` uses `end` on the overview so that Overview is
          not also marked current while the client is on Invoices.
        */}
        <nav
          aria-label="Portal sections"
          className="mt-5 border-t border-white/15 pt-4 lg:hidden"
        >
          <label htmlFor="portal-section" className="label text-white/55">
            Menu
          </label>
          <div className="relative mt-2">
            <select
              id="portal-section"
              value={activeSlug}
              onChange={(event) => {
                const slug = event.target.value;
                navigate(slug ? `${base}/${slug}` : base);
              }}
              className="min-h-11 w-full appearance-none border border-white/25 bg-[#1b1b1b] px-4 pr-11 text-base text-white [color-scheme:dark] outline-none transition-colors focus:border-sand focus:ring-2 focus:ring-sand/30"
            >
              {PAGES.map(([slug, label]) => (
                <option
                  key={slug}
                  value={slug}
                  className="bg-[#0d0d0d] text-white"
                >
                  {label}
                </option>
              ))}
            </select>
            <svg
              aria-hidden="true"
              viewBox="0 0 20 20"
              fill="none"
              className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-white/65"
            >
              <path
                d="m5 7.5 5 5 5-5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </nav>

        <nav
          aria-label="Portal sections"
          className="mt-8 hidden border-t border-white/15 pt-6 lg:block"
        >
          <ul>
            {PAGES.map(([slug, label]) => (
              <li key={slug}>
                <NavLink
                  to={slug ? `${base}/${slug}` : base}
                  end={slug === ""}
                  className={({ isActive }) =>
                    "block border-l px-4 py-3 text-base transition-colors " +
                    (isActive
                      ? "border-sand bg-white/[0.08] text-white"
                      : "border-transparent text-white/65 hover:bg-white/[0.04] hover:text-white")
                  }
                >
                  {label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        {/*
          Who you are, and then signing out underneath it — which is the order
          the user asked for and the right one. Sign out used to sit above the
          name, in the row with the brand lockup, so the most destructive
          control on the page was the most prominent thing after the logo and
          the person's own name was an afterthought at the bottom. Now the
          block reads as one thing: this is you, and this is how you leave.

          `mt-auto` only from `lg`, where the aside is a full-height column. On
          a phone it is a normal block in the flow and pushing it down would
          leave a gap above the content.
        */}
        <div className="mt-5 flex min-w-0 items-center justify-between gap-4 border-t border-white/15 pt-4 lg:mt-auto lg:block lg:pt-5">
          {me && (
            <p
              className="min-w-0 truncate text-sm text-white/80"
              title={me.fullName}
            >
              {me.fullName}
            </p>
          )}
          <Button
            tone="quiet"
            onClick={() => void signOut()}
            className="shrink-0 justify-end px-0 text-white/65 hover:text-white lg:mt-2 lg:w-full lg:justify-start lg:px-3"
          >
            Sign out
          </Button>
        </div>
      </aside>

      <div className="min-w-0">
        <main className="mx-auto w-full max-w-[86rem] px-6 py-10 lg:px-12 lg:py-14">
          {resource.state === "loading" && <SkeletonRows rows={5} />}

          {resource.state === "absent" && <AbsentNotice />}

          {resource.state === "failed" && (
            <Failed
              message={resource.message}
              correlationId={resource.correlationId}
              onRetry={reload}
            />
          )}

          {resource.state === "ready" && (
            <motion.div
              // One settle for the whole composition, not a stagger down the
              // page. A client sees this rarely and a sequence would make the
              // firm look slow rather than considered.
              //
              // Keyed on nothing: the settle plays per mount, so moving
              // between pages does not replay it. Changing engagement
              // remounts the resource and it does, which is right — that is
              // a new composition, not a new tab of the same one.
              variants={portalSettles}
              initial={reduced ? false : "initial"}
              animate="settled"
            >
              <Routes>
                <Route
                  index
                  element={
                    <PortalOverview
                      engagementId={engagementId}
                      portal={resource.data}
                      base={base}
                      respond={respond}
                      reload={reload}
                    />
                  }
                />
                <Route
                  path="actions"
                  element={
                    <PortalActions
                      portal={resource.data}
                      respond={respond}
                      reload={reload}
                    />
                  }
                />
                <Route
                  path="progress"
                  element={<PortalProgress portal={resource.data} />}
                />
                <Route
                  path="deliverables"
                  element={
                    <PortalDeliverables portal={resource.data} reload={reload} />
                  }
                />
                <Route
                  path="files"
                  element={<PortalFiles portal={resource.data} />}
                />
                <Route
                  path="invoices"
                  element={<PortalInvoices engagementId={engagementId} />}
                />
                {/* An unknown sub-path is not an error worth a screen. The
                    overview is where a client who mistyped should land. */}
                <Route
                  path="*"
                  element={
                    <PortalOverview
                      engagementId={engagementId}
                      portal={resource.data}
                      base={base}
                      respond={respond}
                      reload={reload}
                    />
                  }
                />
              </Routes>
            </motion.div>
          )}
        </main>

        <footer className="border-t border-line px-6 py-6 lg:px-12">
          <p className="mx-auto max-w-[86rem] text-2xs leading-4 text-muted">
            Everything shown here has been released to you by your engagement
            team. Documents remain available while the engagement is open.
          </p>
        </footer>
      </div>
    </div>
  );
}
